import React, { useEffect, useState, useMemo } from "react";
import {
    Box,
    TextField,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PageHeader1 from "../../components/common/PageHeader1";
import axios from "axios";
import { handleUnauthorized } from "../../TokenAuth/auth";
import {
    formatUTCDateForDataGrid,
    formatUTCDate,
} from "../../utils/dateUtils";
import * as XLSX from "xlsx";

function formatDuration(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

function CashbackReportExport() {
    const [rowsData, setRowsData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [pagination, setPagination] = useState({
        page: 0,
        pageSize: 10,
    });

    const [total, setTotal] = useState(0);

    // 🔍 SEARCH
    const [searchType, setSearchType] = useState("mobileNumber");
    const [searchValue, setSearchValue] = useState("");
    const [appliedSearchType, setAppliedSearchType] =
        useState("mobileNumber");
    const [appliedSearchValue, setAppliedSearchValue] = useState("");

    // 📅 DATE
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [isExporting, setIsExporting] = useState(false);

    const token = localStorage.getItem("astroVani-admin-token");

    // ================= API =================
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const params = {
                    page: pagination.page + 1,
                    limit: pagination.pageSize,
                };

                // 🔍 SEARCH PARAM
                if (appliedSearchValue) {
                    if (appliedSearchType === "mobileNumber") {
                        params.mobileNumber = appliedSearchValue;
                    } else if (appliedSearchType === "orderId") {
                        params.orderId = appliedSearchValue;
                    }
                }

                // 📅 DATE PARAMS
                if (startDate) params.startDate = startDate;
                if (endDate) params.endDate = endDate;

                const res = await axios.get(
                    `${process.env.REACT_APP_BASEURL}/admin/cashbackReport`,
                    {
                        params,
                        headers: { Authorization: token },
                    }
                );

                setRowsData(res?.data?.data || []);

                // ✅ FIXED TOTAL
                setTotal(res?.data?.paginationDetail?.totalDocs || 0);
            } catch (err) {
                if (err?.response?.status === 401) handleUnauthorized();
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [pagination, appliedSearchType, appliedSearchValue, startDate, endDate]);

    // ================= APPLY =================
    const handleApply = () => {
        setAppliedSearchType(searchType);
        setAppliedSearchValue(searchValue);
        setPagination((prev) => ({ ...prev, page: 0 }));
    };

    // ================= RESET =================
    const handleReset = () => {
        setSearchType("mobileNumber");
        setSearchValue("");
        setAppliedSearchType("mobileNumber");
        setAppliedSearchValue("");
        setStartDate("");
        setEndDate("");
        setPagination({ page: 0, pageSize: 10 });
    };

    // ================= EXPORT =================
    const handleExport = async () => {
        setIsExporting(true);
        try {
            const params = new URLSearchParams();

            if (startDate) params.append("startDate", startDate);
            if (endDate) params.append("endDate", endDate);

            const response = await fetch(
                `${process.env.REACT_APP_BASEURL}/admin/cashbackReportExport?${params.toString()}`,
                {
                    headers: { Authorization: token },
                }
            );

            if (!response.ok) {
                if (response.status === 401) handleUnauthorized();
                throw new Error("Export failed");
            }

            const result = await response.json();
            const data = result?.data || result || [];

            const exportData = data.map((item, i) => ({
                "S.No": i + 1,
                "User Name": item.userName || "Deleted User",
                "Mobile Number": item.mobileNumber ? String(item.mobileNumber) : "-",
                "Order ID": item.orderId ? String(item.orderId) : "N/A",
                "Cashback Received": item.amount ?? 0,
                "Type": item.type || "-",
                "Cashback Type": item.cashbackType || "-",
                "Cashback Value": item.cashbackValue ?? 0,
                "Duration": formatDuration(item.duration || 0),
                "Astrologer": item.astrologerName || "Deleted Astrologer",
                "Created Date": formatUTCDate(item.createdAt, { format: "date", separator: "/" }),
                "Created Time": formatUTCDate(item.createdAt, { format: "time", showSeconds: true }),
            }));

            const worksheet = XLSX.utils.json_to_sheet(exportData);

            // Set explicit column widths to prevent '#####' in Excel
            worksheet["!cols"] = [
                { wch: 8 },  // S.No
                { wch: 22 }, // User Name
                { wch: 18 }, // Mobile Number
                { wch: 26 }, // Order ID
                { wch: 18 }, // Cashback Received
                { wch: 14 }, // Type
                { wch: 18 }, // Cashback Type
                { wch: 16 }, // Cashback Value
                { wch: 14 }, // Duration
                { wch: 22 }, // Astrologer
                { wch: 16 }, // Created Date
                { wch: 16 }, // Created Time
            ];

            // Ensure Mobile Number and Order ID are strictly formatted as text to avoid scientific notation
            Object.keys(worksheet).forEach((cellKey) => {
                if (cellKey.startsWith("!")) return;
                const col = cellKey.replace(/[0-9]/g, "");
                if ((col === "C" || col === "D") && cellKey !== "C1" && cellKey !== "D1") {
                    const cell = worksheet[cellKey];
                    if (cell) {
                        cell.t = "s";
                        cell.z = "@";
                        cell.v = String(cell.v ?? "");
                    }
                }
            });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "CashbackReport");

            const today = new Date().toISOString().split("T")[0];
            XLSX.writeFile(workbook, `cashback-report_${today}.xlsx`);
        } catch (err) {
            console.error(err);
            alert("Export failed");
        } finally {
            setIsExporting(false);
        }
    };

    // ================= COLUMNS =================
    const columns = [
        { field: "sono", headerName: "S.No", width: 80 },
        {
            field: "userName", headerName: "User Name", width: 180,
            renderCell: (params) => params.value || "Deleted User"
        },
        {
            field: "mobileNumber", headerName: "Mobile", width: 150,
            renderCell: (params) => params.value || "-"
        },
        { field: "orderId", headerName: "Order ID", width: 180 },
        {
            field: "amount",
            headerName: "Cashback Received",
            width: 120,
        },
        { field: "type", headerName: "Type", width: 120 },
        { field: "cashbackType", headerName: "Cashback Type", width: 120 },
        { field: "cashbackValue", headerName: "Cashback Value", width: 120 },
        {
            field: "duration",
            headerName: "Duration",
            width: 120,
            renderCell: (params) => formatDuration(params.value || 0)
        },
        {
            field: "astrologerName", headerName: "Astrologer", width: 180,
            renderCell: (params) => params.value || "Deleted Astrologer"
        },
        {
            field: "createdAt",
            headerName: "Created At",
            width: 180,
            valueFormatter: formatUTCDateForDataGrid,
        },
    ];

    const rows = useMemo(() => {
        return rowsData.map((item, i) => ({
            ...item,
            id: item._id,
            sono: i + 1 + pagination.page * pagination.pageSize,
        }));
    }, [rowsData, pagination]);

    // ================= UI =================
    return (
        <div className="container-xxl">
            <PageHeader1 pagetitle="Cashback Report" />

            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                {/* 🔽 DROPDOWN */}
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Search By</InputLabel>
                    <Select
                        value={searchType}
                        label="Search By"
                        onChange={(e) => setSearchType(e.target.value)}
                    >
                        <MenuItem value="mobileNumber">Mobile Number</MenuItem>
                        <MenuItem value="orderId">Order ID</MenuItem>
                    </Select>
                </FormControl>

                {/* 🔍 INPUT */}
                <TextField
                    size="small"
                    placeholder={
                        searchType === "mobileNumber"
                            ? "Enter mobile number..."
                            : "Enter order ID..."
                    }
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") handleApply();
                    }}
                />

                {/* 📅 DATE */}
                <TextField
                    type="date"
                    size="small"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                />

                <TextField
                    type="date"
                    size="small"
                    label="End Date"
                    InputLabelProps={{ shrink: true }}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                />

                <Button variant="contained" onClick={handleApply}>
                    Apply
                </Button>

                <Button variant="outlined" onClick={handleReset}>
                    Reset
                </Button>

                <Button
                    variant="contained"
                    onClick={handleExport}
                    disabled={isExporting}
                >
                    {isExporting ? (
                        <>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            Exporting...
                        </>
                    ) : (
                        "Export"
                    )}
                </Button>
            </Box>

            {/* ✅ SINGLE PAGINATION (DataGrid only) */}
            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                rowCount={total}
                loading={loading}
                paginationMode="server"
                paginationModel={pagination}
                onPaginationModelChange={setPagination}
                pageSizeOptions={[10, 25, 50]}
                pagination
            />
        </div>
    );
}

export default CashbackReportExport;